import { useEffect, useRef, useState, useCallback } from "react";
import {
  Download, ArrowRight, Shield, Brain, Heart, TrendingUp, Users, Building2,
  Handshake, Sparkles, Clock, Lock, Award, Target, Zap, LineChart,
  ChevronDown, Phone, Mail, Globe, Trophy, Briefcase, HeartHandshake,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";

const LOGO = "https://customer-assets.emergentagent.com/job_gen-connect-11/artifacts/5jr50pac_gh%20logo.png";

/* ============================ SLIDE WRAPPER ============================ */
function Slide({ id, index, total, eyebrow, children, className = "", noCounter = false }) {
  return (
    <section
      id={`slide-${index}`}
      data-slide={index}
      className={`gse-slide relative w-full min-h-screen flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-20 ${className}`}
    >
      {/* Grain overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay"
           style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      {/* Slide chrome (hidden in PDF via .pdf-hide) */}
      {!noCounter && (
        <div className="pdf-hide absolute top-8 left-6 sm:left-12 lg:left-20 flex items-center gap-3 text-white/50 text-xs tracking-[0.28em] uppercase font-medium z-10">
          <img src={LOGO} alt="GH" className="h-6 w-6 rounded-full object-cover ring-1 ring-white/20" />
          <span>Generational Health × GSE Worldwide</span>
        </div>
      )}

      {!noCounter && (
        <div className="pdf-hide absolute top-8 right-6 sm:right-12 lg:right-20 text-white/40 text-xs tracking-widest font-mono z-10">
          {String(index).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
      )}

      {eyebrow && (
        <div className="mb-6 text-teal-300 text-xs sm:text-sm tracking-[0.32em] uppercase font-semibold">
          {eyebrow}
        </div>
      )}

      <div className="relative z-10 w-full max-w-6xl mx-auto">
        {children}
      </div>
    </section>
  );
}

/* ================================ DECK ================================= */
export default function GSEDeck() {
  const containerRef = useRef(null);
  const [current, setCurrent] = useState(1);
  const [exporting, setExporting] = useState(false);
  const { lang } = useLanguage();
  const total = 15;

  useEffect(() => {
    document.title = "GSE Worldwide × Generational Health — Confidential Proposal";
    // Track current slide via IntersectionObserver
    const slides = document.querySelectorAll(".gse-slide");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.5) {
            const idx = Number(e.target.getAttribute("data-slide"));
            if (idx) setCurrent(idx);
          }
        });
      },
      { threshold: [0.5] }
    );
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  const goTo = useCallback((n) => {
    const el = document.getElementById(`slide-${n}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (exporting) return;
      if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        goTo(Math.min(current + 1, total));
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        goTo(Math.max(current - 1, 1));
      } else if (e.key === "Home") {
        goTo(1);
      } else if (e.key === "End") {
        goTo(total);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, goTo, exporting, total]);

  const downloadPDF = useCallback(async () => {
    setExporting(true);
    try {
      const html2pdfModule = await import("html2pdf.js");
      const html2pdf = html2pdfModule.default;
      const el = containerRef.current;
      if (!el) return;
      // Add class to hide chrome during export
      el.classList.add("pdf-export");
      await new Promise((r) => setTimeout(r, 250));
      const opt = {
        margin: 0,
        filename: `GSE-Worldwide-x-Generational-Health${lang === "en" ? "" : `-${lang.toUpperCase()}`}.pdf`,
        image: { type: "jpeg", quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#0A1F3D", logging: false },
        jsPDF: { unit: "in", format: [13.33, 7.5], orientation: "landscape" },
        pagebreak: { mode: ["css", "legacy"], before: ".gse-slide" },
      };
      await html2pdf().set(opt).from(el).save();
      el.classList.remove("pdf-export");
    } catch (err) {
      console.error("PDF export failed", err);
      alert("PDF export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [lang]);

  const progress = ((current - 1) / (total - 1)) * 100;

  return (
    <div className="gse-root bg-navy text-white font-sans antialiased selection:bg-teal-400 selection:text-navy" data-testid="gse-deck-root">
      {/* Global styles for this deck only */}
      <style>{`
        .gse-root { font-family: 'Manrope', system-ui, sans-serif; background:#0A1F3D; }
        .gse-display { font-family: 'Fraunces', 'Playfair Display', Georgia, serif; letter-spacing:-0.02em; line-height:1.02; }
        .gse-slide { scroll-snap-align: start; overflow: hidden; }
        .gse-scroll { scroll-snap-type: y mandatory; scroll-behavior: smooth; height: 100vh; overflow-y: auto; }
        .gse-glass { background: rgba(255,255,255,0.04); backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); border: 1px solid rgba(255,255,255,0.08); }
        .gse-glow { position: absolute; border-radius: 9999px; filter: blur(120px); opacity:0.35; pointer-events:none; }
        .gse-num { font-variant-numeric: tabular-nums; font-family: 'Fraunces', serif; }
        .gse-hair { background: linear-gradient(90deg, transparent, rgba(20,184,166,0.6), transparent); height:1px; }
        .gse-chip { border:1px solid rgba(20,184,166,0.35); background: rgba(20,184,166,0.08); color: #5EEAD4; }
        .gse-btn-primary { background: linear-gradient(180deg, #14B8A6, #0D9488); box-shadow: 0 12px 40px -8px rgba(20,184,166,0.55); }
        .gse-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 16px 44px -6px rgba(20,184,166,0.7); }
        .gse-grid-bg {
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 64px 64px;
        }
        .pdf-export .pdf-hide { display: none !important; }
        .pdf-export .gse-slide { page-break-after: always; break-after: page; min-height: 7.5in !important; height: 7.5in !important; }
        .pdf-export { height: auto !important; overflow: visible !important; scroll-snap-type: none !important; }
        @media print {
          .pdf-hide { display: none !important; }
        }
      `}</style>

      {/* ============ Fixed HUD ============ */}
      <div className="pdf-hide fixed top-0 left-0 right-0 z-50">
        {/* Progress bar */}
        <div className="h-[2px] bg-white/5">
          <div
            className="h-full bg-gradient-to-r from-teal-400 via-teal-300 to-teal-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Floating controls */}
      <div className="pdf-hide fixed bottom-6 right-6 z-50 flex items-center gap-3">
        <LanguageSwitcher variant="dark" testIdPrefix="gse-lang" />
        <div className="gse-glass px-4 py-2 rounded-full text-xs tracking-widest font-mono text-white/70" dir="ltr" data-no-translate>
          {String(current).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        <button
          onClick={downloadPDF}
          disabled={exporting}
          data-testid="gse-download-pdf"
          className="gse-btn-primary text-navy font-semibold text-sm rounded-full pl-4 pr-5 py-2.5 flex items-center gap-2 transition-all disabled:opacity-60"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Preparing PDF…" : "Download PDF"}
        </button>
      </div>

      {/* Prev/Next arrows */}
      <div className="pdf-hide fixed bottom-6 left-6 z-50 flex items-center gap-2">
        <button
          onClick={() => goTo(Math.max(current - 1, 1))}
          className="gse-glass w-10 h-10 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
          aria-label="Previous slide"
          data-testid="gse-prev"
        >
          <ChevronDown className="w-4 h-4 rotate-180" />
        </button>
        <button
          onClick={() => goTo(Math.min(current + 1, total))}
          className="gse-glass w-10 h-10 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
          aria-label="Next slide"
          data-testid="gse-next"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* ================ DECK BODY ================ */}
      <div ref={containerRef} className="gse-scroll">

        {/* ---------- Slide 01: COVER ---------- */}
        <Slide index={1} total={total} noCounter>
          <div className="absolute inset-0">
            <div className="gse-grid-bg absolute inset-0 opacity-30" />
            <div className="gse-glow w-[520px] h-[520px] -top-40 -left-40 bg-teal-500" />
            <div className="gse-glow w-[600px] h-[600px] bottom-[-200px] right-[-200px] bg-cyan-400" style={{ opacity: 0.22 }} />
          </div>

          <div className="relative z-10 flex flex-col justify-between h-full">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Generational Health" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20" />
              <div className="text-white/90 text-sm font-semibold tracking-widest uppercase">Generational Health</div>
              <div className="w-px h-5 bg-white/20 mx-2" />
              <div className="text-white/60 text-sm tracking-widest uppercase">Confidential · Q1 2026</div>
            </div>

            <div className="max-w-5xl">
              <div className="gse-chip inline-block px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-semibold mb-8">
                Strategic Wellness Partnership Proposal
              </div>
              <h1 className="gse-display text-6xl sm:text-7xl lg:text-8xl font-black text-white mb-8">
                GSE Worldwide<br />
                <span className="text-teal-300">×</span> Generational Health
              </h1>
              <p className="text-2xl sm:text-3xl text-white/70 max-w-3xl leading-tight font-light">
                An agency that protects <span className="text-white font-semibold">careers</span> —
                not just <span className="line-through text-white/40">contracts</span>.
              </p>
            </div>

            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div className="max-w-md">
                <div className="gse-hair mb-4" />
                <p className="text-white/50 text-sm">
                  Prepared for the leadership of GSE Worldwide.<br />
                  Confidential — Not for external distribution.
                </p>
              </div>
              <div className="text-right">
                <div className="text-white/40 text-xs tracking-[0.28em] uppercase mb-1">Presented by</div>
                <div className="text-white font-semibold">Michelle Coffino</div>
                <div className="text-white/50 text-sm">CEO & Co-Founder, Generational Health</div>
              </div>
            </div>
          </div>
        </Slide>

        {/* ---------- Slide 02: THE THESIS ---------- */}
        <Slide index={2} total={total} eyebrow="The thesis">
          <div className="absolute inset-0">
            <div className="gse-glow w-[500px] h-[500px] top-1/3 -right-40 bg-teal-500" />
          </div>
          <h2 className="gse-display text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-10 leading-[0.95]">
            Every contract represents <span className="text-teal-300">years</span> of recruiting,<br />
            trust, and relationship building.
          </h2>
          <p className="text-xl sm:text-2xl text-white/70 max-w-4xl leading-relaxed font-light">
            The moment a client faces a mental health crisis, an addiction relapse, or an off-field
            incident — <span className="text-white font-medium">that entire investment is at risk.</span>
          </p>
          <div className="mt-14 gse-hair max-w-md" />
          <p className="mt-6 text-white/50 text-lg max-w-3xl">
            Generational Health is the invisible layer that protects the careers, the relationships,
            and the reputations you've spent decades building.
          </p>
        </Slide>

        {/* ---------- Slide 03: THE REALITY ---------- */}
        <Slide index={3} total={total} eyebrow="The hidden cost">
          <div className="absolute inset-0">
            <div className="gse-glow w-[420px] h-[420px] top-0 left-1/3 bg-cyan-400" style={{ opacity: 0.18 }} />
          </div>
          <h2 className="gse-display text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-16 max-w-4xl leading-tight">
            The mental health crisis in elite performance isn't hidden anymore.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "35%", label: "of elite athletes report a mental health issue in a given year", src: "Athletes for Hope, IOC data" },
              { n: "1 in 4", label: "professional athletes will experience depression or anxiety", src: "APA · NCAA studies" },
              { n: "$0", label: "spent by most agencies on integrated 24/7 clinical wellness", src: "Industry benchmark" },
            ].map((s, i) => (
              <div key={i} className="gse-glass rounded-2xl p-8">
                <div className="gse-num text-6xl font-black text-teal-300 mb-4">{s.n}</div>
                <div className="text-white text-lg leading-snug mb-4">{s.label}</div>
                <div className="text-white/40 text-xs tracking-wider uppercase">{s.src}</div>
              </div>
            ))}
          </div>
          <p className="mt-12 text-white/60 text-lg max-w-3xl">
            Every one of these numbers translates into contracts at risk, endorsements paused, and
            years of relationship-building undone by a single crisis.
          </p>
        </Slide>

        {/* ---------- Slide 04: WHAT GSE ALREADY DOES ---------- */}
        <Slide index={4} total={total} eyebrow="What GSE already does brilliantly">
          <h2 className="gse-display text-5xl sm:text-6xl font-black text-white mb-14 max-w-4xl leading-tight">
            GSE doesn't sign clients.<br />
            <span className="text-teal-300">You sign families, teams, brands, and futures.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: <Handshake className="w-6 h-6" />, t: "Long-arc representation", d: "Careers built over decades, not deal cycles." },
              { icon: <Trophy className="w-6 h-6" />, t: "Elite roster access", d: "World-class athletes, coaches, and personalities." },
              { icon: <Building2 className="w-6 h-6" />, t: "Brand partnerships", d: "Category-defining endorsements and IP deals." },
              { icon: <HeartHandshake className="w-6 h-6" />, t: "Relationship equity", d: "Trust that competitors cannot buy or replicate." },
            ].map((c, i) => (
              <div key={i} className="gse-glass rounded-2xl p-7 flex items-start gap-5">
                <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center text-teal-300 flex-shrink-0">
                  {c.icon}
                </div>
                <div>
                  <div className="text-white text-xl font-semibold mb-1">{c.t}</div>
                  <div className="text-white/60 leading-relaxed">{c.d}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-white/50 text-lg max-w-3xl">
            You've built an ecosystem. Now protect its most volatile variable — the human beings inside it.
          </p>
        </Slide>

        {/* ---------- Slide 05: WHAT'S MISSING ---------- */}
        <Slide index={5} total={total} eyebrow="The gap">
          <div className="absolute inset-0">
            <div className="gse-glow w-[500px] h-[500px] bottom-0 left-0 bg-teal-500" style={{ opacity: 0.28 }} />
          </div>
          <h2 className="gse-display text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-12 max-w-5xl leading-[0.95]">
            No agency in the world has a HIPAA-compliant, 24/7 clinical wellness system embedded across its entire ecosystem.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="text-white/40 text-xs tracking-widest uppercase mb-3">Today</div>
              <ul className="space-y-3 text-white/70 text-lg">
                <li>• EAPs no one actually calls</li>
                <li>• Reactive PR crisis playbooks</li>
                <li>• Isolated therapists per athlete</li>
                <li>• Wellness as a checkbox, not infrastructure</li>
              </ul>
            </div>
            <div>
              <div className="text-teal-300 text-xs tracking-widest uppercase mb-3">With Generational Health</div>
              <ul className="space-y-3 text-white text-lg">
                <li className="flex gap-2"><span className="text-teal-300">→</span> Proactive, AI-assisted daily support</li>
                <li className="flex gap-2"><span className="text-teal-300">→</span> Instant clinician escalation, 24/7</li>
                <li className="flex gap-2"><span className="text-teal-300">→</span> One platform, entire agency ecosystem</li>
                <li className="flex gap-2"><span className="text-teal-300">→</span> Wellness as agency-defining IP</li>
              </ul>
            </div>
          </div>
        </Slide>

        {/* ---------- Slide 06: INTRODUCING GH ---------- */}
        <Slide index={6} total={total} eyebrow="Introducing">
          <div className="flex items-center gap-4 mb-8">
            <img src={LOGO} alt="Generational Health" className="h-14 w-14 rounded-2xl object-cover ring-1 ring-teal-400/30" />
            <div>
              <div className="text-white text-3xl font-bold">Generational Health</div>
              <div className="text-teal-300 text-sm tracking-wider uppercase">Your mind. Your power.</div>
            </div>
          </div>
          <h2 className="gse-display text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-10 max-w-5xl leading-tight">
            The HIPAA-compliant mental health platform built on <span className="text-teal-300">patented</span> clinical workflow — for the people the world watches.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <Brain className="w-5 h-5" />, t: "AI Genius Companion", d: "24/7 clinically informed AI" },
              { icon: <Shield className="w-5 h-5" />, t: "Patented Backflow", d: "Right signal, right clinician, right moment" },
              { icon: <Users className="w-5 h-5" />, t: "Licensed Provider Network", d: "Vetted, culturally aware clinicians" },
              { icon: <Lock className="w-5 h-5" />, t: "HIPAA-native", d: "Enterprise-grade privacy" },
            ].map((f, i) => (
              <div key={i} className="gse-glass rounded-xl p-5">
                <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center text-teal-300 mb-4">
                  {f.icon}
                </div>
                <div className="text-white font-semibold mb-1">{f.t}</div>
                <div className="text-white/50 text-sm">{f.d}</div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-white/50 text-lg max-w-3xl">
            Built for high-performance humans by operators who've lived the stakes.
          </p>
        </Slide>

        {/* ---------- Slide 07: ECOSYSTEM PLAY ---------- */}
        <Slide index={7} total={total} eyebrow="The Ecosystem Play">
          <div className="absolute inset-0">
            <div className="gse-grid-bg absolute inset-0 opacity-20" />
          </div>
          <h2 className="gse-display text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-4 leading-[0.95]">
            Not one client at a time.
          </h2>
          <h2 className="gse-display text-5xl sm:text-6xl lg:text-7xl font-black text-teal-300 mb-14 leading-[0.95]">
            The entire GSE ecosystem.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <Trophy className="w-7 h-7" />, k: "01", t: "Athletes", d: "Career longevity + off-field stability" },
              { icon: <Building2 className="w-7 h-7" />, k: "02", t: "Brands", d: "A wellness-first partner story" },
              { icon: <Briefcase className="w-7 h-7" />, k: "03", t: "Employees", d: "Agency-wide benefit no competitor offers" },
            ].map((p, i) => (
              <div key={i} className="gse-glass rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute top-4 right-5 gse-num text-6xl font-black text-white/5">{p.k}</div>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-navy mb-6">
                  {p.icon}
                </div>
                <div className="text-white text-2xl font-bold mb-2">{p.t}</div>
                <div className="text-white/60">{p.d}</div>
              </div>
            ))}
          </div>
        </Slide>

        {/* ---------- Slide 08: ATHLETES ---------- */}
        <Slide index={8} total={total} eyebrow="Pillar 01 · Athletes">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <h2 className="gse-display text-5xl sm:text-6xl font-black text-white leading-[0.95] mb-8">
                Protect the asset.<br />
                <span className="text-teal-300">Elevate the human.</span>
              </h2>
              <p className="text-white/70 text-lg leading-relaxed">
                Every GSE athlete gets a private, HIPAA-compliant wellness portal — from the moment
                they sign. Confidential to them. Career-protecting for GSE.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { icon: <Brain className="w-5 h-5" />, t: "AI companion trained on athlete-specific stressors" },
                { icon: <Phone className="w-5 h-5" />, t: "24/7 crisis line — call a human, in minutes" },
                { icon: <Users className="w-5 h-5" />, t: "Vetted therapists who understand elite performance" },
                { icon: <Shield className="w-5 h-5" />, t: "Zero-agent visibility into personal sessions (HIPAA)" },
                { icon: <Award className="w-5 h-5" />, t: "Recovery, addiction, sleep, family, and identity support" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center text-teal-300 flex-shrink-0">
                    {b.icon}
                  </div>
                  <div className="text-white text-lg">{b.t}</div>
                </div>
              ))}
            </div>
          </div>
        </Slide>

        {/* ---------- Slide 09: BRANDS ---------- */}
        <Slide index={9} total={total} eyebrow="Pillar 02 · Brands">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <h2 className="gse-display text-5xl sm:text-6xl font-black text-white leading-[0.95] mb-8">
                A story brands actually <span className="text-teal-300">want to sponsor.</span>
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-6">
                Wellness is the single most category-agnostic narrative in modern brand marketing.
                GSE becomes the agency that packaged it first.
              </p>
              <p className="text-white/50 text-base leading-relaxed">
                Endorsement conversations shift from "reach × impressions" to
                <span className="text-white"> "reach × meaning."</span>
              </p>
            </div>
            <div className="space-y-4">
              {[
                { t: "Co-branded wellness campaigns tied to real client stories" },
                { t: "Rights to reference \"GSE athletes powered by Generational Health\"" },
                { t: "Access to aggregate, anonymized wellness impact data (HIPAA-safe)" },
                { t: "First-look inventory on new GH partnership formats" },
                { t: "Brand-safe crisis mitigation infrastructure" },
              ].map((b, i) => (
                <div key={i} className="gse-glass rounded-xl p-4 flex items-center gap-4">
                  <div className="w-8 h-8 rounded-md bg-teal-500 text-navy flex items-center justify-center font-black text-sm gse-num">
                    {i + 1}
                  </div>
                  <div className="text-white text-lg">{b.t}</div>
                </div>
              ))}
            </div>
          </div>
        </Slide>

        {/* ---------- Slide 10: EMPLOYEES ---------- */}
        <Slide index={10} total={total} eyebrow="Pillar 03 · GSE Employees">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <h2 className="gse-display text-5xl sm:text-6xl font-black text-white leading-[0.95] mb-8">
                The best <span className="text-teal-300">retention</span> lever in agency M&A.
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-6">
                Agents burn out. Recruiters carry the emotional weight of every client. Support staff
                absorb the pressure of every crisis. GSE becomes the first agency to give every
                employee — from mailroom to managing partner — the same wellness stack the athletes get.
              </p>
              <p className="text-teal-300 text-base tracking-wider uppercase font-semibold">
                A benefit no competitor can match — until they build one themselves.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { n: "24/7", l: "clinical access" },
                { n: "0", l: "cost to employee" },
                { n: "100%", l: "HIPAA-compliant" },
                { n: "1", l: "unified agency platform" },
              ].map((s, i) => (
                <div key={i} className="gse-glass rounded-2xl p-8 text-center">
                  <div className="gse-num text-5xl font-black text-teal-300 mb-2">{s.n}</div>
                  <div className="text-white/70 text-sm tracking-wider uppercase">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </Slide>

        {/* ---------- Slide 11: AGGREGATE INSIGHTS ---------- */}
        <Slide index={11} total={total} eyebrow="Agency Intelligence">
          <div className="absolute inset-0">
            <div className="gse-glow w-[500px] h-[500px] top-0 right-0 bg-cyan-400" style={{ opacity: 0.2 }} />
          </div>
          <h2 className="gse-display text-5xl sm:text-6xl font-black text-white mb-10 max-w-5xl leading-tight">
            An agency dashboard that knows nothing about individuals — and everything about the ecosystem.
          </h2>
          <p className="text-white/70 text-xl leading-relaxed max-w-4xl mb-12">
            GSE leadership sees the pulse of the entire roster and staff at a systems level —
            without ever seeing a name, a session, or a note. HIPAA is not compromised. Ever.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <LineChart className="w-6 h-6" />, t: "Ecosystem wellness index", d: "Agency-wide temperature check" },
              { icon: <TrendingUp className="w-6 h-6" />, t: "Utilization trends", d: "By region, sport, department" },
              { icon: <Zap className="w-6 h-6" />, t: "Early-signal alerts", d: "Anonymous escalation flags to safeguard careers" },
            ].map((c, i) => (
              <div key={i} className="gse-glass rounded-2xl p-7">
                <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center text-teal-300 mb-4">
                  {c.icon}
                </div>
                <div className="text-white text-xl font-bold mb-2">{c.t}</div>
                <div className="text-white/60">{c.d}</div>
              </div>
            ))}
          </div>
        </Slide>

        {/* ---------- Slide 12: THE MATH ---------- */}
        <Slide index={12} total={total} eyebrow="The Career Longevity Multiplier">
          <h2 className="gse-display text-5xl sm:text-6xl font-black text-white mb-12 max-w-4xl leading-tight">
            One saved career pays for the entire agency's wellness stack. Ten times over.
          </h2>
          <div className="gse-glass rounded-3xl p-8 sm:p-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { n: "$50M+", l: "Lifetime value of a top-tier client contract" },
                { n: "×", l: "" },
                { n: "1", l: "Career preserved through GH intervention" },
                { n: "= ROI", l: "That any agency wellness budget can't rival" },
              ].map((r, i) => (
                <div key={i} className="text-center">
                  <div className="gse-num text-5xl sm:text-6xl font-black text-teal-300 mb-3">{r.n}</div>
                  <div className="text-white/60 text-sm tracking-wide">{r.l}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-10 text-white/50 text-lg max-w-3xl">
            This isn't a benefits line item. It's the highest-leverage insurance policy any modern agency can hold.
          </p>
        </Slide>

        {/* ---------- Slide 13: ROLLOUT ---------- */}
        <Slide index={13} total={total} eyebrow="90-Day Rollout">
          <h2 className="gse-display text-5xl sm:text-6xl font-black text-white mb-14 leading-tight">
            From handshake to <span className="text-teal-300">agency-wide activation.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { p: "Days 0–30", t: "Foundation", items: ["Legal + HIPAA BAA", "GSE-branded portal setup", "Executive onboarding"] },
              { p: "Days 31–60", t: "Activation", items: ["Athlete roster invitation waves", "Employee benefits launch", "Provider network alignment"] },
              { p: "Days 61–90", t: "Amplify", items: ["Brand partner narrative unlock", "Aggregate dashboard live", "First quarter review"] },
            ].map((s, i) => (
              <div key={i} className="gse-glass rounded-2xl p-7 relative">
                <div className="gse-num text-6xl font-black text-teal-300/10 absolute top-3 right-5">0{i + 1}</div>
                <div className="text-teal-300 text-xs tracking-[0.28em] uppercase font-semibold mb-3">{s.p}</div>
                <div className="text-white text-2xl font-bold mb-5">{s.t}</div>
                <ul className="space-y-2">
                  {s.items.map((it, j) => (
                    <li key={j} className="text-white/70 flex items-start gap-2">
                      <span className="text-teal-300 mt-1">→</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Slide>

        {/* ---------- Slide 14: WHY NOW ---------- */}
        <Slide index={14} total={total} eyebrow="Why now, why GSE">
          <div className="absolute inset-0">
            <div className="gse-glow w-[600px] h-[600px] bottom-0 right-0 bg-teal-500" style={{ opacity: 0.28 }} />
          </div>
          <h2 className="gse-display text-6xl sm:text-7xl font-black text-white leading-[0.95] mb-10">
            The first agency to<br />
            embed wellness as<br />
            <span className="text-teal-300">infrastructure</span><br />
            defines the next decade.
          </h2>
          <p className="text-xl text-white/70 max-w-3xl leading-relaxed">
            GSE has the roster, the relationships, and the courage. Generational Health has the
            clinical infrastructure, the AI, and the patents. Together — we don't compete in the
            agency market. We <span className="text-white font-semibold">redefine it.</span>
          </p>
        </Slide>

        {/* ---------- Slide 15: THE ASK ---------- */}
        <Slide index={15} total={total} eyebrow="The Ask" noCounter>
          <div className="absolute inset-0">
            <div className="gse-grid-bg absolute inset-0 opacity-20" />
            <div className="gse-glow w-[600px] h-[600px] top-1/4 left-1/4 bg-teal-500" style={{ opacity: 0.28 }} />
          </div>
          <div className="relative z-10">
            <h2 className="gse-display text-6xl sm:text-7xl lg:text-8xl font-black text-white mb-10 leading-[0.92]">
              Let's build the world's<br />
              first <span className="text-teal-300">wellness-native</span><br />
              talent agency.
            </h2>
            <div className="gse-glass rounded-3xl p-8 sm:p-12 max-w-3xl">
              <div className="text-teal-300 text-xs tracking-[0.32em] uppercase font-semibold mb-6">Next Step</div>
              <p className="text-white text-2xl sm:text-3xl leading-tight mb-8 gse-display">
                A 45-minute working session with GSE leadership to scope Phase One.
              </p>
              <div className="gse-hair mb-8" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <div className="text-white/40 text-xs tracking-widest uppercase mb-2">Contact</div>
                  <div className="text-white font-semibold">Michelle Coffino</div>
                  <div className="text-white/60 text-sm">CEO & Co-Founder</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs tracking-widest uppercase mb-2 flex items-center gap-2"><Mail className="w-3 h-3" /> Email</div>
                  <div className="text-white">mickey@generationalh.com</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs tracking-widest uppercase mb-2 flex items-center gap-2"><Globe className="w-3 h-3" /> Web</div>
                  <div className="text-white">generationalh.com</div>
                </div>
              </div>
            </div>
            <div className="mt-16 flex items-center gap-4">
              <img src={LOGO} alt="Generational Health" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20" />
              <div className="text-white/50 text-sm">
                Generational Health · Confidential proposal · Prepared for GSE Worldwide
              </div>
            </div>
          </div>
        </Slide>
      </div>
    </div>
  );
}
